/**
 * Calendar dates — the ONLY place a `YYYY-MM-DD` string is derived.
 *
 * Project rule (standing engineering rules, "Dates"): all calendar-date derivation
 * goes through this module. Never derive a date string from raw `new Date()`
 * getters or from `toISOString().slice(0, 10)`.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §5): `toISOString()` is UTC. In
 * Europe/Brussels (UTC+1/+2) every write made after 22:00 or 23:00 local lands on
 * *tomorrow's* date. The atelier works evenings; return deadlines, loan periods and
 * booking days are legal-ish facts on paper. A booking silently shifted by one day
 * is invisible in code review and expensive in an atelier that is chasing a
 * designer's return deadline.
 *
 * Domain rule: concepts meaning "a day" — return deadlines, loan periods, booking
 * days, agreement dates — are stored as local calendar dates (`YYYY-MM-DD`), not as
 * timestamps. Instants (movements, uploads, signatures) stay timestamptz.
 */
/**
 * The atelier's operational timezone. Deliberately explicit and NOT the host's
 * system zone: CI runners, the Scaleway region and a stylist's phone abroad must
 * all agree on what "today" means for a deadline in Antwerp.
 */
export const ATELIER_TIME_ZONE = 'Europe/Brussels';
let defaultTimeZone = ATELIER_TIME_ZONE;
/** Test/tenant override. Returns the previous value so callers can restore it. */
export function setDefaultTimeZone(timeZone) {
    const previous = defaultTimeZone;
    defaultTimeZone = timeZone;
    return previous;
}
export function getDefaultTimeZone() {
    return defaultTimeZone;
}
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export function isISODate(value) {
    if (typeof value !== 'string' || !ISO_DATE_RE.test(value))
        return false;
    const { year, month, day } = splitISODate(value);
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return false;
    // Reject 2026-02-30 and friends by round-tripping through UTC.
    const d = new Date(Date.UTC(year, month - 1, day));
    return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}
function splitISODate(iso) {
    return {
        year: Number(iso.slice(0, 4)),
        month: Number(iso.slice(5, 7)),
        day: Number(iso.slice(8, 10)),
    };
}
function assertISODate(iso) {
    if (!isISODate(iso))
        throw new TypeError(`Not a calendar date (YYYY-MM-DD): ${JSON.stringify(iso)}`);
}
/**
 * An **instant**, not a calendar date: full RFC-3339 UTC, for logs, movements,
 * uploads and signatures.
 *
 * The distinction this module exists to keep visible:
 *  - "when exactly did this happen" -> instantISO()  -> stored as timestamptz
 *  - "which day is this deadline"   -> todayISO()    -> stored as a date (YYYY-MM-DD)
 *
 * Deriving the second from the first by slicing is the bug (see the tests). Calling
 * instantISO() for an audit timestamp is correct and always has been; it lives here
 * so both concepts sit side by side and the lint ban on raw toISOString() can stay
 * absolute.
 */
export function instantISO(at = new Date()) {
    if (!(at instanceof Date) || Number.isNaN(at.getTime())) {
        throw new TypeError('instantISO requires a valid Date');
    }
    return at.toISOString();
}
const formatterCache = new Map();
function formatterFor(timeZone) {
    let f = formatterCache.get(timeZone);
    if (!f) {
        // formatToParts, not toLocaleDateString: locale-independent, order-independent.
        f = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        formatterCache.set(timeZone, f);
    }
    return f;
}
/** Convert an instant to the calendar date it falls on in a given timezone. */
export function toISODate(instant, options = {}) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new TypeError('toISODate requires a valid Date');
    }
    const parts = formatterFor(options.timeZone ?? defaultTimeZone).formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
}
/** Today's calendar date in the atelier timezone (or an override). */
export function todayISO(options = {}) {
    return toISODate(options.now ?? new Date(), options);
}
const labelCache = new Map();
/**
 * An instant rendered for a HUMAN READER, in the atelier's zone, WITH the zone named:
 * `2036-05-09 12:00 CEST`.
 *
 * Why this exists rather than `toLocaleString()` at the point of use: a screen may
 * render in the reader's own zone (that is a feature — the reader knows their zone),
 * but a FIXED ARTIFACT cannot. A loan agreement PDF is read years later by someone
 * who was not there, so an unlabelled wall-clock time is ambiguous and a bare `Z`
 * string is unfriendly. Naming the zone costs six characters and removes the
 * question.
 *
 * Still an INSTANT throughout — this formats one for display and never derives a
 * calendar day from it. `toISODate()` remains the only way to do that.
 */
export function formatInstant(instant, options = {}) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new TypeError('formatInstant requires a valid Date');
    }
    const timeZone = options.timeZone ?? defaultTimeZone;
    let formatter = labelCache.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZoneName: 'short',
        });
        labelCache.set(timeZone, formatter);
    }
    const parts = formatter.formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    // Assembled from parts rather than trusting the locale's join order, the same
    // reasoning as toISODate above.
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')} ${get('timeZoneName')}`;
}
/**
 * AN INSTANT FOR A HUMAN, WITH ITS ZONE SPELLED OUT: `1 Oct 2026 13:00 CEST (UTC+2)` (0.1.524).
 *
 * The maintainer, 2 Oct 2026: *"make sure that it clearly shows which time zone the time is in,
 * next to the time … so that it's always obvious"* — asked after a client's reply read "11:00"
 * that was really 13:00 in Antwerp (the screen printed Postgres's UTC text raw).
 *
 * ⚠️ THE ABBREVIATION IS LOOKED UP, NOT ASKED OF `Intl`. `timeZoneName: 'short'` is engine-defined
 * for an `en` locale outside the US and comes back as "GMT+2" from V8 and as "CEST" elsewhere — the
 * same instant would read two ways on two devices. The OFFSET is reliable (`longOffset` →
 * "GMT+02:00"), so the offset is read and the name comes from a table that knows only the
 * atelier's zone: Europe/Brussels at +1 is CET, at +2 is CEST. Any other zone or offset prints the
 * offset alone — a guessed name would be invented data (Guardrail 9).
 */
const ZONE_NAMES = {
    'Europe/Brussels': { '+1': 'CET', '+2': 'CEST' },
};
const zoneClockCache = new Map();
function zoneClock(timeZone) {
    let f = zoneClockCache.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-GB', {
            timeZone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZoneName: 'longOffset',
        });
        zoneClockCache.set(timeZone, f);
    }
    return f;
}
/** `UTC+2`, `UTC−3:30`, `UTC` — the offset of `timeZone` at `instant`. */
export function utcOffsetLabel(instant, options = {}) {
    const timeZone = options.timeZone ?? defaultTimeZone;
    const raw = zoneClock(timeZone).formatToParts(instant).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
    const m = /^GMT([+-])(\d{2}):(\d{2})$/.exec(raw);
    if (m === null || (m[2] === '00' && m[3] === '00'))
        return 'UTC';
    const hours = String(Number(m[2]));
    return `UTC${m[1] ?? '+'}${hours}${m[3] === '00' ? '' : `:${m[3] ?? ''}`}`;
}
export function formatInstantWithZone(instant, options = {}) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new TypeError('formatInstantWithZone requires a valid Date');
    }
    const timeZone = options.timeZone ?? defaultTimeZone;
    const parts = zoneClock(timeZone).formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const offset = utcOffsetLabel(instant, { timeZone });
    const name = ZONE_NAMES[timeZone]?.[offset.replace(/^UTC/, '')];
    const zone = name === undefined ? offset : `${name} (${offset})`;
    // The DAY is the zone's own day (`toISODate` in that zone) — 23:30 UTC is tomorrow in Antwerp.
    return `${formatDay(toISODate(instant, { timeZone }))} ${get('hour')}:${get('minute')} ${zone}`;
}
/**
 * A CALENDAR DATE rendered for a human: `14 Aug 2026` (redesign — the frames say "14 Aug",
 * never "2026-08-14"). Takes a `YYYY-MM-DD` string, NOT an instant — a calendar date has no
 * time and no zone, so it is formatted at UTC midnight where the day cannot roll. This is the
 * display twin of `toISODate`: that one parses days in, this one prints them out, and neither
 * touches the availability engine, which stays on the ISO strings.
 */
/**
 * ⚠️ `en-US`, ASSEMBLED FROM PARTS — not `en-GB`. British ICU renders September's short month
 * as "Sept" (four letters, the one odd one out), which reads as a typo beside "Aug"/"Oct".
 * en-US is uniformly three letters, and assembling from parts lets us keep the frames'
 * day-month-year order regardless of the locale's own join order.
 */
const dayPartsFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
function dayParts(at) {
    const parts = dayPartsFmt.formatToParts(at);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return { d: get('day'), mon: get('month'), y: get('year') };
}
export function formatDay(iso) {
    if (!isISODate(iso))
        throw new TypeError('formatDay requires a YYYY-MM-DD date');
    const [y, m, d] = iso.split('-').map(Number);
    const p = dayParts(new Date(Date.UTC(y, m - 1, d)));
    return `${p.d} ${p.mon} ${p.y}`;
}
/**
 * A CALENDAR-DATE RANGE, collapsed the way the frames draw it: `14–16 Aug 2026` when the
 * month and year are shared, `28 Aug – 3 Sep 2026` within a year, `28 Dec 2025 – 3 Jan 2026`
 * across one. A single-day range is just the day. Both bounds are inclusive `YYYY-MM-DD`.
 */
export function formatDayRange(startsOn, endsOn) {
    if (!isISODate(startsOn) || !isISODate(endsOn))
        throw new TypeError('formatDayRange requires YYYY-MM-DD dates');
    if (startsOn === endsOn)
        return formatDay(startsOn);
    const [sy, sm, sd] = startsOn.split('-').map(Number);
    const [ey, em, ed] = endsOn.split('-').map(Number);
    const s = dayParts(new Date(Date.UTC(sy, sm - 1, sd)));
    const e = dayParts(new Date(Date.UTC(ey, em - 1, ed)));
    if (sy === ey && sm === em)
        return `${s.d}–${e.d} ${s.mon} ${s.y}`;
    if (sy === ey)
        return `${s.d} ${s.mon} – ${e.d} ${e.mon} ${s.y}`;
    return `${s.d} ${s.mon} ${s.y} – ${e.d} ${e.mon} ${e.y}`;
}
/**
 * The dashboard band's long form — `Thursday 13 August` (frame 8b beside the title). No
 * year: the band names TODAY, and today's year is noise. Through the same UTC-anchored
 * parts machinery as `formatDay`, so no `new Date()` getter can smuggle the zone back in.
 */
const dayNameFmt = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
export function formatDayName(iso) {
    if (!isISODate(iso))
        throw new TypeError('formatDayName requires a YYYY-MM-DD date');
    const [y, m, d] = iso.split('-').map(Number);
    return dayNameFmt.format(new Date(Date.UTC(y, m - 1, d)));
}
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
export function isWallClock(value) {
    if (typeof value !== 'string' || !WALL_CLOCK.test(value))
        return false;
    // Reject 2036-02-31T10:00 and friends: the regex only proves the shape.
    const [, y, m, d, h, min] = WALL_CLOCK.exec(value);
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    const probe = new Date(Date.UTC(year, month - 1, day, Number(h), Number(min)));
    return (probe.getUTCFullYear() === year &&
        probe.getUTCMonth() === month - 1 &&
        probe.getUTCDate() === day &&
        Number(h) < 24 &&
        Number(min) < 60);
}
/** The zone's offset from UTC, in ms, AT a given instant (DST-aware). */
function zoneOffsetMs(instant, timeZone) {
    const parts = wallClockFormatterFor(timeZone).formatToParts(instant);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    // What the clock on the wall reads, re-read as if it were UTC. The difference
    // between that and the true instant IS the offset.
    const asIfUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return asIfUtc - instant.getTime();
}
const wallClockCache = new Map();
function wallClockFormatterFor(timeZone) {
    let f = wallClockCache.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
        wallClockCache.set(timeZone, f);
    }
    return f;
}
/**
 * A wall-clock stamp IN A ZONE → the instant it names. `2036-05-10T16:15` in
 * Europe/Brussels is one specific moment; the same string in Tokyo is another.
 *
 * WHY THE SERVER MUST NOT SKIP THIS. A phone in another zone typing "16:15" for an
 * Antwerp fitting means 16:15 in ANTWERP. Interpreting the string in the device's
 * zone would book the wrong hour for a travelling stylist, silently. So the zone is
 * always explicit and defaults to the atelier's.
 *
 * THE TWO PASSES ARE THE DST FIX, not defensive noise. The offset depends on the
 * instant, and the instant is what we are solving for — so the first pass guesses
 * with the offset at the naive time, and the second re-solves with the offset that
 * actually applies there. On the two ambiguous hours a year:
 *   * spring-forward gap (02:30 does not exist): resolves forward, to 03:30 real
 *     time — the next moment that does exist, never a throw.
 *   * autumn overlap (02:30 happens twice): resolves to the SECOND — the later
 *     instant, 02:30 CET rather than 02:30 CEST.
 * Both behaviours are MEASURED, not reasoned: the first draft of this comment
 * claimed the overlap picked the earlier instant, and the test said otherwise. They
 * are pinned by assertions, so a change is a red test rather than a silent hour.
 * Neither case throws — a booking form must not dead-end on one hour a year.
 */
export function instantFromWallClock(wall, options = {}) {
    if (!isWallClock(wall)) {
        throw new TypeError(`instantFromWallClock requires YYYY-MM-DDTHH:MM, got ${JSON.stringify(wall)}`);
    }
    const timeZone = options.timeZone ?? defaultTimeZone;
    const naive = Date.parse(`${wall}:00Z`);
    const firstGuess = naive - zoneOffsetMs(new Date(naive), timeZone);
    const refined = naive - zoneOffsetMs(new Date(firstGuess), timeZone);
    return new Date(refined).toISOString();
}
/**
 * The inverse: an instant → the wall-clock stamp it shows in a zone. Feeds
 * `<input type="datetime-local">`, which cannot take an instant.
 */
export function wallClockFromInstant(instant, options = {}) {
    if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
        throw new TypeError('wallClockFromInstant requires a valid Date');
    }
    const parts = wallClockFormatterFor(options.timeZone ?? defaultTimeZone).formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    // 'en-CA' hour12:false renders midnight as '24' in some runtimes; normalise, or a
    // whole-day booking would round-trip to the wrong day.
    const hour = get('hour') === '24' ? '00' : get('hour');
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}
/**
 * Local midnight starting `iso`, as an instant — the lower bound of a whole-day
 * booking. Its upper bound is `dayStartInstant(addDays(iso, 1))`, exclusive.
 */
export function dayStartInstant(iso, options = {}) {
    assertISODate(iso);
    return instantFromWallClock(`${iso}T00:00`, options);
}
/**
 * A whole-day booking as the half-open instant range it really is (ruling B):
 * `('2036-05-10', '2036-05-14')` → [10th 00:00 local, 15th 00:00 local).
 *
 * `endsOn` is INCLUSIVE — the last day the booking holds — because that is what a
 * human means by "the 10th to the 14th" and what every day-granular reader in this
 * codebase already means. The +1 day to reach the exclusive bound happens exactly
 * here, once, rather than at each call site where it could be forgotten.
 *
 * Lives in shared because the web creates these ranges and the server validates
 * them; two implementations of the same +1 would drift on the day it mattered.
 */
export function wholeDayRange(startsOn, endsOn, options = {}) {
    assertISODate(startsOn);
    assertISODate(endsOn);
    if (endsOn < startsOn) {
        throw new RangeError(`wholeDayRange: ${endsOn} ends before ${startsOn} starts`);
    }
    return {
        startsAt: dayStartInstant(startsOn, options),
        endsAt: dayStartInstant(addDays(endsOn, 1), options),
    };
}
/**
 * Calendar arithmetic on `YYYY-MM-DD`. Uses UTC noon internally so a DST
 * transition can never shunt the result into the neighbouring day.
 */
export function addDays(iso, days) {
    assertISODate(iso);
    if (!Number.isInteger(days))
        throw new TypeError('addDays requires an integer');
    const { year, month, day } = splitISODate(iso);
    const anchor = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    anchor.setUTCDate(anchor.getUTCDate() + days);
    const y = String(anchor.getUTCFullYear()).padStart(4, '0');
    const m = String(anchor.getUTCMonth() + 1).padStart(2, '0');
    const d = String(anchor.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** Whole calendar days from `from` to `to`. Negative when `to` is earlier. */
export function diffDays(from, to) {
    assertISODate(from);
    assertISODate(to);
    const a = splitISODate(from);
    const b = splitISODate(to);
    const ms = Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day);
    return Math.round(ms / 86_400_000);
}
export function compareISODate(a, b) {
    assertISODate(a);
    assertISODate(b);
    return a < b ? -1 : a > b ? 1 : 0;
}
export function urgencyBucket(deadline, options = {}) {
    const days = diffDays(todayISO(options), deadline);
    if (days < 0)
        return 'overdue';
    if (days <= 7)
        return 'due-within-7';
    if (days <= 14)
        return 'due-8-to-14';
    return 'later';
}
//# sourceMappingURL=date.js.map